/**
 * ErrorBoundary Component
 * Catches JavaScript errors anywhere in child component tree.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from '@utils/logger';
import styles from './ErrorBoundary.module.css';

/**
 * Error boundary state.
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error boundary props.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback component */
  fallback?: ReactNode;
  /** Callback when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to show error details (development only) */
  showDetails?: boolean;
}

/**
 * Error Boundary component for catching and handling React errors.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Updates state when an error is thrown.
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Logs error information.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error
    logger.error('ErrorBoundary caught an error', error, { componentStack: errorInfo.componentStack });

    // Update state with error info
    this.setState({ errorInfo });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // TODO: Send to error tracking service (e.g., Sentry)
    // if (import.meta.env.PROD) {
    //   Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    // }
  }

  /**
   * Resets the error state.
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Reloads the page.
   */
  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, showDetails = import.meta.env.DEV } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.iconWrapper}>
              <ErrorIcon />
            </div>
            <h1 className={styles.title}>Something went wrong</h1>
            <p className={styles.message}>
              We're sorry, but something unexpected happened. Please try again or contact support if
              the problem persists.
            </p>

            <div className={styles.actions}>
              <button className={styles.primaryButton} onClick={this.handleReset}>
                Try Again
              </button>
              <button className={styles.secondaryButton} onClick={this.handleReload}>
                Reload Page
              </button>
            </div>

            {showDetails && error && (
              <details className={styles.details}>
                <summary className={styles.detailsSummary}>Error Details</summary>
                <div className={styles.detailsContent}>
                  <p className={styles.errorName}>{error.name}</p>
                  <p className={styles.errorMessage}>{error.message}</p>
                  {errorInfo && (
                    <pre className={styles.stackTrace}>
                      {errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return children;
  }
}

/**
 * Error icon component.
 */
const ErrorIcon = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="3" />
    <path
      d="M32 20V36"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <circle cx="32" cy="44" r="2" fill="currentColor" />
  </svg>
);

/**
 * Higher-order component for wrapping components with error boundary.
 */
export const withErrorBoundary = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) => {
  const WithErrorBoundary = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundary.displayName = `WithErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithErrorBoundary;
};

